import type { IApiClient, OpenDialogOptions, Unsubscribe } from '@tn/ui'
import { generateGcc, cancelGccJob, getGccJob, runTnOnRetainedWorkforce, combineGccTnAnalyses, seedGccDemo, resetGccDemo } from './gcc/gccApi'
import type {
  Project,
  Settings,
  GenerateOutsideInPayload,
  GenerateInsideOutPayload,
  RunInsideOutStagePayload,
  GenerateProgressEvent,
  ApplyFeedbackPayload,
  FeedbackProgressEvent,
  RegenTab4Payload,
  DemoModeState,
  DemoDatasetInfo,
  OutsideInJobState,
  InsideOutJobState,
  FeedbackJobState,
  RegenTab4JobState,
  StagedResult,
  GenerateGccPayload,
  GccJobState,
  RunTnOnRetainedPayload,
  CombineGccTnPayload,
  HeadcountReconciliationResult,
  DropdownOptions,
  User,
  AIProvider,
  BrandColorLookupResult,
} from '@shared/types'
import type {
  LicenseStatus,
  ActivationResult,
  LicenseInfo,
} from '@shared/license-types'
import type { ExcelParseResult } from '@shared/column-matching'

/**
 * Browser implementation of IApiClient. Talks to the Fastify server via fetch
 * for request/response and streams AI progress via POST+SSE (fetch streaming,
 * not EventSource — EventSource is GET-only, AI routes need POST bodies).
 *
 * Basic-auth is handled by the browser's own credential cache once the user
 * authenticates on the first API call.
 *
 * Desktop-only capabilities that don't map cleanly (file dialog, arbitrary
 * file reads) are implemented in terms of browser primitives — a dynamically
 * inserted <input type=file> drives openFileDialog, and readFile resolves from
 * a session-scoped cache of the chosen File objects.
 */
export class HttpApiClient implements IApiClient {
  readonly platform = 'web' as const
  readonly supportsPasswordExport = false
  private readonly base: string
  private readonly progressSubs = new Set<(e: GenerateProgressEvent) => void>()
  private readonly feedbackSubs = new Set<(e: FeedbackProgressEvent) => void>()
  private activeOutsideInJobId: string | null = null
  private outsideInCancelRequested = false
  private readonly regenTab4Subs = new Set<(e: FeedbackProgressEvent) => void>()
  // openFileDialog returns opaque tokens; readFile looks up the File by token.
  private readonly fileCache = new Map<string, File>()

  constructor(base = '/api') {
    this.base = base.replace(/\/$/, '')
  }

  // ── Projects ─────────────────────────────────────────────────────────
  async listProjects(options?: { mineOnly?: boolean }): Promise<Project[]> {
    return this.getJson<Project[]>(options?.mineOnly ? '/projects?mine=true' : '/projects')
  }
  async getProject(id: string): Promise<Project | null> {
    const res = await fetch(`${this.base}/projects/${encodeURIComponent(id)}`, {
      credentials: 'include',
    })
    if (res.status === 404) return null
    if (!res.ok) throw await this.toError(res)
    return (await res.json()) as Project
  }
  async saveProject(project: Project): Promise<string> {
    const existing = await this.getProject(project.id).catch(() => null)
    const method = existing ? 'PUT' : 'POST'
    const url = existing
      ? `${this.base}/projects/${encodeURIComponent(project.id)}`
      : `${this.base}/projects`
    const res = await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    })
    if (!res.ok) throw await this.toError(res)
    const body = (await res.json()) as { id: string }
    return body.id
  }
  async deleteProject(id: string): Promise<void> {
    const res = await fetch(`${this.base}/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) throw await this.toError(res)
  }

  // ── Settings ─────────────────────────────────────────────────────────
  async getSettings(): Promise<Settings> {
    return this.getJson<Settings>('/settings')
  }
  async setSettings(partial: Partial<Settings>): Promise<void> {
    // Server merges over current, so we can pass the partial directly.
    const res = await fetch(`${this.base}/settings`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...(await this.getSettings()), ...partial }),
    })
    if (!res.ok) throw await this.toError(res)
  }

  // ── AI Generation ────────────────────────────────────────────────────
  async generateOutsideIn(
    payload: GenerateOutsideInPayload
  ): Promise<{ jobId: string; error?: string }> {
    this.outsideInCancelRequested = false
    try {
      const result = await this.postJson<{ jobId: string }>('/ai/outside-in', payload)
      this.activeOutsideInJobId = result.jobId
      if (this.outsideInCancelRequested) {
        await this.cancelOutsideInJob(result.jobId)
        return { jobId: result.jobId, error: 'Cancelled' }
      }
      return { jobId: result.jobId }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return { jobId: '', error: 'Cancelled' }
      throw err
    }
  }

  async cancelOutsideInJob(jobId: string): Promise<void> {
    this.outsideInCancelRequested = true
    this.activeOutsideInJobId = null
    try {
      await fetch(`${this.base}/ai/outside-in/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch { /* best-effort */ }
  }

  async getOutsideInJob(jobId: string): Promise<OutsideInJobState | null> {
    const res = await fetch(`${this.base}/ai/outside-in/jobs/${encodeURIComponent(jobId)}`, {
      credentials: 'include',
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()) as OutsideInJobState
  }

  async checkInsideOutHeadcount(files: Array<{ base64: string; name: string }>): Promise<HeadcountReconciliationResult> {
    return this.postJson<HeadcountReconciliationResult>('/ai/inside-out/check-headcount', { files })
  }

  async generateInsideOut(
    payload: GenerateInsideOutPayload
  ): Promise<{ jobId: string; error?: string }> {
    const result = await this.postJson<{ jobId: string }>('/ai/inside-out', payload)
    return { jobId: result.jobId }
  }

  async cancelInsideOutJob(jobId: string): Promise<void> {
    try {
      await fetch(`${this.base}/ai/inside-out/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch { /* best-effort */ }
  }

  async runInsideOutStage(
    payload: RunInsideOutStagePayload,
    signal?: AbortSignal
  ): Promise<{ result?: StagedResult; error?: string }> {
    const res = await fetch(`${this.base}/ai/inside-out/stage`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string }
      return { error: body.error ?? `HTTP ${res.status}` }
    }

    const body = await res.json() as { result?: StagedResult; jobId?: string }

    // Stages 1–2 return synchronously. Stages 3–6 return a jobId and run async
    // to avoid the 240-second Azure Container App HTTP timeout on large documents.
    if (!body.jobId) return { result: body.result }

    const { jobId } = body
    let cancelRequested = false
    const cancelServerJob = async (): Promise<void> => {
      if (cancelRequested) return
      cancelRequested = true
      await fetch(`${this.base}/ai/inside-out/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => null)
    }
    // Cancel the server job as soon as the caller aborts, not on the next poll tick.
    signal?.addEventListener('abort', () => { void cancelServerJob() }, { once: true })
    while (true) {
      if (signal?.aborted) { await cancelServerJob(); return { error: 'Cancelled.' } }
      await new Promise<void>((resolve) => setTimeout(resolve, 3_000))
      if (signal?.aborted) { await cancelServerJob(); return { error: 'Cancelled.' } }
      const pollRes = await fetch(
        `${this.base}/ai/inside-out/stage-jobs/${encodeURIComponent(jobId)}`,
        { credentials: 'include', signal }
      ).catch(() => null)
      if (!pollRes) continue
      if (!pollRes.ok) {
        const err = await pollRes.json().catch(() => ({})) as { error?: string }
        return { error: err.error ?? `HTTP ${pollRes.status}` }
      }
      const job = await pollRes.json() as { status: string; error?: string; result?: StagedResult }
      if (job.status === 'succeeded') return { result: job.result }
      if (job.status === 'failed') return { error: job.error ?? 'Stage failed.' }
      // pending or running — keep polling
    }
  }

  async getStageOutputs(projectId: string): Promise<NonNullable<Project['precomputed']>> {
    const res = await fetch(`${this.base}/ai/stage-outputs/${encodeURIComponent(projectId)}`, {
      credentials: 'include',
    })
    if (!res.ok) throw new Error(`Failed to load stage outputs (HTTP ${res.status})`)
    return res.json() as Promise<NonNullable<Project['precomputed']>>
  }

  async finalizeInsideOut(projectId: string): Promise<{ error?: string }> {
    const res = await fetch(`${this.base}/ai/inside-out/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ projectId }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string }
      return { error: body.error ?? `HTTP ${res.status}` }
    }
    return {}
  }

  async downloadStageOutput(projectId: string, stageIndex: number, format: 'xlsx' | 'pdf', filename: string): Promise<void> {
    const res = await fetch(`${this.base}/ai/stage-outputs/${encodeURIComponent(projectId)}/${stageIndex}/${format}`, {
      credentials: 'include',
      cache: 'no-store',
    })
    if (!res.ok) throw new Error('Output not found — run the stage first.')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  keepAlive(): void {
    void fetch(`${this.base.replace('/api', '')}/healthz`, { credentials: 'include' }).catch(() => {})
  }

  async getInsideOutJob(jobId: string): Promise<InsideOutJobState | null> {
    const res = await fetch(`${this.base}/ai/inside-out/jobs/${encodeURIComponent(jobId)}`, {
      credentials: 'include',
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()) as InsideOutJobState
  }

  async getMyActiveJobs(): Promise<Array<{ jobId: string; projectId: string; type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback'; clientName: string; color: string }>> {
    try {
      const body = await this.getJson<{ jobs: Array<{ jobId: string; projectId: string; type: 'outside-in' | 'inside-out' | 'gcc' | 'feedback'; clientName: string; color: string }> }>('/ai/my-active-jobs')
      return body.jobs
    } catch {
      return []
    }
  }

  // ── GCC ──────────────────────────────────────────────────────────────
  async generateGcc(payload: GenerateGccPayload): Promise<{ jobId: string; error?: string }> {
    return generateGcc((path, body) => this.postJson(path, body), payload)
  }

  async cancelGccJob(jobId: string): Promise<void> {
    return cancelGccJob(this.base, jobId)
  }

  async getGccJob(jobId: string): Promise<GccJobState | null> {
    return getGccJob(this.base, jobId)
  }

  async runTnOnRetainedWorkforce(payload: RunTnOnRetainedPayload): Promise<{ jobId: string; projectId: string }> {
    return runTnOnRetainedWorkforce((path, body) => this.postJson(path, body), payload)
  }

  async combineGccTnAnalyses(payload: CombineGccTnPayload): Promise<{ projectId: string }> {
    return combineGccTnAnalyses((path, body) => this.postJson(path, body), payload)
  }

  async seedGccDemo(): Promise<{ seeded: number }> {
    return seedGccDemo((path, body) => this.postJson(path, body))
  }

  async resetGccDemo(): Promise<{ deleted: number }> {
    return resetGccDemo((path) => this.deleteJson(path))
  }

  onGenerateProgress(callback: (event: GenerateProgressEvent) => void): Unsubscribe {
    this.progressSubs.add(callback)
    return () => this.progressSubs.delete(callback)
  }
  async testConnection(provider: string, _apiKey: string): Promise<boolean> {
    // Web server resolves the key from env — we only send the provider.
    const res = await fetch(`${this.base}/ai/test-connection`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider }),
    })
    if (!res.ok) return false
    const body = (await res.json()) as { ok?: boolean }
    return !!body.ok
  }
  async lookupBrandColors(companyName: string, provider: AIProvider, _apiKey: string): Promise<BrandColorLookupResult> {
    // Web server resolves the key from env — we only send companyName/provider.
    return this.postJson<BrandColorLookupResult>('/ai/lookup-brand-colors', { companyName, provider })
  }
  async applyFeedback(
    payload: ApplyFeedbackPayload
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.postJson<{ jobId: string }>('/ai/feedback', payload)
    return this.pollFeedbackJob(result.jobId)
  }
  onFeedbackProgress(callback: (event: FeedbackProgressEvent) => void): Unsubscribe {
    this.feedbackSubs.add(callback)
    return () => this.feedbackSubs.delete(callback)
  }
  async startFeedbackJob(payload: ApplyFeedbackPayload): Promise<{ jobId?: string; error?: string }> {
    try {
      const result = await this.postJson<{ jobId: string }>('/ai/feedback', payload)
      return { jobId: result.jobId }
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) }
    }
  }
  async getFeedbackJob(jobId: string): Promise<FeedbackJobState | null> {
    try {
      const res = await fetch(`${this.base}/ai/feedback/jobs/${encodeURIComponent(jobId)}`, {
        credentials: 'include',
      })
      if (res.status === 404) return null
      if (!res.ok) return null
      return await res.json() as FeedbackJobState
    } catch {
      return null
    }
  }
  async regenTab4(payload: RegenTab4Payload): Promise<{ success: boolean; error?: string }> {
    const result = await this.postJson<{ jobId: string }>('/ai/regen-tab4', payload)
    return this.pollRegenTab4Job(result.jobId)
  }
  onRegenTab4Progress(callback: (event: FeedbackProgressEvent) => void): Unsubscribe {
    this.regenTab4Subs.add(callback)
    return () => this.regenTab4Subs.delete(callback)
  }

  // ── Files ────────────────────────────────────────────────────────────
  async openFileDialog(options: OpenDialogOptions): Promise<string | string[] | null> {
    const input = document.createElement('input')
    input.type = 'file'
    input.style.display = 'none'
    input.multiple = !!options.properties?.includes('multiSelections')
    const exts = (options.filters || [])
      .flatMap((f) => f.extensions)
      .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`))
    if (exts.length) input.accept = Array.from(new Set(exts)).join(',')

    return new Promise((resolvePromise) => {
      input.addEventListener(
        'change',
        () => {
          const files = input.files ? Array.from(input.files) : []
          if (!files.length) {
            document.body.removeChild(input)
            resolvePromise(null)
            return
          }
          const tokens = files.map((file) => {
            const token = `web://file/${crypto.randomUUID()}`
            this.fileCache.set(token, file)
            return token
          })
          document.body.removeChild(input)
          resolvePromise(input.multiple ? tokens : tokens[0])
        },
        { once: true }
      )
      // If the user cancels the dialog we get no change event; the browser fires
      // no cancel signal reliably across vendors. That's fine — the Promise just
      // stays unresolved until the next interaction garbage-collects the input.
      document.body.appendChild(input)
      input.click()
    })
  }

  async readFile(filePath: string): Promise<{ base64: string; name: string }> {
    const file = this.fileCache.get(filePath)
    if (!file) {
      throw new Error('File handle expired or not found. Please re-select the file.')
    }
    const arrayBuffer = await file.arrayBuffer()
    return {
      base64: arrayBufferToBase64(arrayBuffer),
      name: file.name,
    }
  }

  async parseExcel(base64: string, columnHints?: Partial<Record<string, string>>): Promise<ExcelParseResult> {
    const res = await fetch(`${this.base}/files/parse-excel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64, columnHints }),
    })
    // A 400 here means "we read the file but could not use it" and carries the
    // same structured body as a success, so unwrap it instead of throwing —
    // that structure is what lets the UI name the offending column. Anything
    // else is a genuine transport/server failure.
    const body = await res.json().catch(() => null)
    if (body && typeof body === 'object' && 'data' in body && 'errors' in body) {
      return body as ExcelParseResult
    }
    if (!res.ok) {
      const message =
        body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
          ? (body as { error: string }).error
          : `Request failed (${res.status})`
      throw new Error(message)
    }
    throw new Error('Unexpected response from the Excel parser.')
  }

  async exportHtml(projectId: string): Promise<void> {
    await this.downloadFromApi(`/files/projects/${encodeURIComponent(projectId)}/export/html`)
  }
  async exportHtmlProtected(projectId: string, password: string): Promise<void> {
    await this.downloadFromApi(
      `/files/projects/${encodeURIComponent(projectId)}/export/html-protected?password=${encodeURIComponent(password)}`
    )
  }
  async exportPdf(projectId: string): Promise<void> {
    const res = await fetch(
      `${this.base}/files/projects/${encodeURIComponent(projectId)}/export/pdf`,
      { credentials: 'include' }
    )
    if (!res.ok) throw await this.toError(res)
  }
  async exportPdfProtected(projectId: string, password: string): Promise<void> {
    const res = await fetch(
      `${this.base}/files/projects/${encodeURIComponent(projectId)}/export/pdf-protected`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      }
    )
    if (!res.ok) throw await this.toError(res)
  }
  async getTemplatePath(): Promise<string> {
    const body = await this.getJson<{ path: string }>('/files/template')
    return body.path
  }
  async downloadTemplate(templateId: string): Promise<void> {
    await this.downloadFromApi(`/files/templates/${encodeURIComponent(templateId)}/download`)
  }
  async getDashboardHtml(projectId: string): Promise<string> {
    const res = await fetch(
      `${this.base}/files/projects/${encodeURIComponent(projectId)}/dashboard-html`,
      { credentials: 'include' }
    )
    if (!res.ok) throw await this.toError(res)
    return res.text()
  }
  async uploadToWeb(_projectId: string): Promise<{ success: boolean; error?: string }> {
    return { success: false, error: 'Not available on web' }
  }

  // ── License ──────────────────────────────────────────────────────────
  async getLicenseStatus(): Promise<LicenseStatus> {
    return this.getJson<LicenseStatus>('/license/status')
  }
  async activateLicense(_licenseData: string): Promise<ActivationResult> {
    return { success: true }
  }
  async getLicenseInfo(): Promise<LicenseInfo | null> {
    return this.getJson<LicenseInfo>('/license/info').catch(() => null)
  }
  async resetLicense(): Promise<void> {
    await fetch(`${this.base}/license/reset`, {
      method: 'POST',
      credentials: 'include',
    })
  }
  onLicenseCheck(_callback: (status: LicenseStatus) => void): Unsubscribe {
    // License never changes on the web build (permanent valid stub) — no push
    // channel to subscribe to. Return a no-op unsubscribe for API parity.
    return () => {}
  }

  // ── Demo ─────────────────────────────────────────────────────────────
  async getDemoState(): Promise<DemoModeState> {
    return this.getJson<DemoModeState>('/demo/state')
  }
  async setDemoActive(active: boolean): Promise<{ success: boolean; error?: string }> {
    const res = await fetch(`${this.base}/demo/active`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    })
    return (await res.json()) as { success: boolean; error?: string }
  }
  async listDemoDatasets(): Promise<{
    datasets: DemoDatasetInfo[]
    installedIds: string[]
  }> {
    return this.getJson<{ datasets: DemoDatasetInfo[]; installedIds: string[] }>(
      '/demo/datasets'
    )
  }

  // ── Internals ────────────────────────────────────────────────────────
  private async getJson<T>(path: string): Promise<T> {
    const res = await fetch(`${this.base}${path}`, { credentials: 'include' })
    if (!res.ok) throw await this.toError(res)
    return (await res.json()) as T
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${this.base}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw await this.toError(res)
    return (await res.json()) as T
  }

  private async deleteJson<T>(path: string): Promise<T> {
    const res = await fetch(`${this.base}${path}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) throw await this.toError(res)
    return (await res.json()) as T
  }

  private async toError(res: Response): Promise<Error> {
    // ESO session gate returns 401 JSON for /api/* when the eso_session cookie
    // is missing or expired. Surface a refresh hint so the SPA can prompt the
    // user; a full reload remounts EsoAuthGate which re-runs the dance.
    if (res.status === 401) {
      return new Error('Session may have expired — please refresh the page and try again')
    }
    // The status goes on its own line so GccErrorDetails shows it as a labelled
    // tooltip section under the one-line summary.
    const status = `HTTP status: ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`
    let body: { error?: unknown; message?: unknown; statusCode?: unknown }
    try {
      body = (await res.json()) as typeof body
    } catch {
      // Non-JSON body (e.g. a proxy HTML page) — give a useful message
      if (res.status >= 500) {
        return new Error(`Server error — please try again. If the problem persists, contact support.\n${status}`)
      }
      return new Error(`HTTP ${res.status}`)
    }
    const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v : undefined)
    // Fastify's default error body ({ statusCode, error, message }) puts the
    // generic status phrase ("Internal Server Error") in `error` and the real
    // cause in `message`; our routes send the specific text in `error`.
    const specific =
      typeof body?.statusCode === 'number'
        ? text(body.message) ?? text(body.error)
        : text(body?.error) ?? text(body?.message)
    return new Error(specific ? `${specific}\n${status}` : `HTTP ${res.status}`)
  }

  /**
   * Download a file from an authenticated API endpoint via blob + <a download>.
   * The server sets Content-Disposition so the browser uses the right filename.
   */
  private async downloadFromApi(path: string): Promise<void> {
    const res = await fetch(`${this.base}${path}`, { credentials: 'include' })
    if (!res.ok) throw await this.toError(res)
    const filename = parseFilename(res.headers.get('Content-Disposition')) || 'download'
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }


  private async pollFeedbackJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    const seen = new Map<string, string>()
    let notFoundRetries = 0
    const maxNotFoundRetries = 5

    while (true) {
      const res = await fetch(`${this.base}/ai/feedback/jobs/${encodeURIComponent(jobId)}`, {
        credentials: 'include',
      })

      if (res.status === 404) {
        if (notFoundRetries < maxNotFoundRetries) {
          notFoundRetries++
          await delay(1000)
          continue
        }
        throw new Error('Job not found — the server may have restarted. Please try again.')
      }

      if (!res.ok) throw await this.toError(res)
      if ((res.headers.get('content-type') ?? '').includes('text/html')) {
        throw new Error('Session may have expired — please refresh the page and try again')
      }

      notFoundRetries = 0
      const job = (await res.json()) as FeedbackJobState
      for (const event of job.progress) {
        const signature = `${event.status}|${event.error || ''}`
        if (seen.get(event.label) === signature) continue
        seen.set(event.label, signature)
        for (const fn of this.feedbackSubs) fn(event)
      }

      if (job.status === 'succeeded') return { success: true }
      if (job.status === 'failed') return { success: false, error: job.error || 'Generation failed' }

      await delay(2000)
    }
  }

  private async pollRegenTab4Job(jobId: string): Promise<{ success: boolean; error?: string }> {
    const seen = new Map<string, string>()
    let notFoundRetries = 0
    const maxNotFoundRetries = 5

    while (true) {
      const res = await fetch(`${this.base}/ai/regen-tab4/jobs/${encodeURIComponent(jobId)}`, {
        credentials: 'include',
      })

      if (res.status === 404) {
        if (notFoundRetries < maxNotFoundRetries) {
          notFoundRetries++
          await delay(1000)
          continue
        }
        throw new Error('Job not found — the server may have restarted. Please try again.')
      }

      if (!res.ok) throw await this.toError(res)
      if ((res.headers.get('content-type') ?? '').includes('text/html')) {
        throw new Error('Session may have expired — please refresh the page and try again')
      }

      notFoundRetries = 0
      const job = (await res.json()) as RegenTab4JobState
      for (const event of job.progress) {
        const signature = `${event.status}|${event.error || ''}`
        if (seen.get(event.label) === signature) continue
        seen.set(event.label, signature)
        for (const fn of this.regenTab4Subs) fn(event)
      }

      if (job.status === 'succeeded') return { success: true }
      if (job.status === 'failed') return { success: false, error: job.error || 'Generation failed' }

      await delay(2000)
    }
  }

  // ── Verbiage ─────────────────────────────────────────────────────────────
  async getDropdownOptions(): Promise<DropdownOptions> {
    return this.getJson('/config/dropdown-options')
  }

  // ── Admin ──────────────────────────────────────────────────────────────────
  async getSessionEmail(): Promise<string> {
    const res = await fetch('/auth/session', { credentials: 'include' })
    if (!res.ok) return ''
    const data = (await res.json()) as { email?: string }
    return data.email ?? ''
  }

  async checkAdminAccess(): Promise<{ isAdmin: boolean; email: string }> {
    const res = await fetch(`${this.base}/admin/check`, { credentials: 'include' })
    if (!res.ok) return { isAdmin: false, email: '' }
    const data = (await res.json()) as { isAdmin?: boolean; email?: string }
    return { isAdmin: data.isAdmin ?? false, email: data.email ?? '' }
  }

  async getAdminDashboards(): Promise<Project[]> {
    return this.getJson<Project[]>('/admin/dashboards')
  }

  async getAdminDashboard(id: string): Promise<Project | null> {
    const res = await fetch(`${this.base}/admin/dashboards/${encodeURIComponent(id)}`, { credentials: 'include' })
    if (res.status === 404 || res.status === 403) return null
    if (!res.ok) throw await this.toError(res)
    return (await res.json()) as Project
  }

  async getAdminUsers(): Promise<User[]> {
    return this.getJson('/admin/users')
  }

  async downloadAdminReport(filename: string): Promise<void> {
    const res = await fetch(`${this.base}/admin/export`, { credentials: 'include' })
    if (!res.ok) throw new Error('Export failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async searchUsers(query: string): Promise<{ email: string }[]> {
    if (!query.trim()) return []
    const res = await fetch(`${this.base}/users/search?q=${encodeURIComponent(query)}`, { credentials: 'include' })
    if (!res.ok) return []
    return (await res.json()) as { email: string }[]
  }

  async shareProject(projectId: string, email: string): Promise<void> {
    const res = await fetch(`${this.base}/projects/${encodeURIComponent(projectId)}/share`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) throw await this.toError(res)
  }
}

// ── Module helpers ─────────────────────────────────────────────────────

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)))
  }
  return btoa(binary)
}

function parseFilename(header: string | null): string | null {
  if (!header) return null
  // Prefer RFC 6266 `filename*=UTF-8''...` (percent-encoded, carries non-ASCII)
  // over the plain ASCII `filename="..."` fallback.
  const ext = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (ext) {
    try { return decodeURIComponent(ext[1].trim()) } catch { /* fall through */ }
  }
  const match = /filename="([^"]+)"/.exec(header) || /filename=([^;]+)/.exec(header)
  return match ? match[1].trim() : null
}


function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
